/**
 * Экран проверяющего: отчёты хозяйств без отметки проверяющего.
 *
 * Отчёт уже принят как факт; проверка — необязательное действие поверх него
 * (журнал решений №28). Поэтому здесь нет KPI «ждут проверки» и долей
 * «проверено»: экран — инструмент, а не счётчик долга.
 *
 * Третий вход в ту же модель данных. Экран хозяйства входит снизу, экран
 * кампании — сверху, этот — сбоку: его вопрос не «что сделано», а «о чём
 * заявлено и ещё не разобрано». Числа под ним те же контурные факты, поэтому
 * очередь не может разойтись со сводкой кампании.
 *
 * Что зафиксировано формой, а не договорённостью:
 *
 * * **работа без отчёта в очередь не попадает.** Проверка ссылается на отчёт и
 *   без него не существует; «нет данных» и «не проверено» — разные вещи, и обе
 *   стоят на экране раздельными числами;
 * * **спор показан обеими сторонами.** «Что сказало хозяйство» и «что сказал
 *   проверяющий» стоят рядом в колонках равной ширины: одна колонка поверх
 *   другой читалась бы как «эта сторона права», а спор не решён;
 * * **причина обязательна для спора.** Поле стоит в одном ряду с кнопками, а
 *   не за «дополнительно»: правило живёт на сервере, и предлагать нажать
 *   кнопку, которую он не примет, — мешать человеку;
 * * **роль вносящего названа вслух.** Менеджер вправе проверять, но менеджер
 *   бывает автором проверяемого отчёта, и слово «проверено» без этого
 *   выглядело бы независимее, чем есть.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAnalysisContext } from "../../analysis/context";
import { api, ApiError } from "../../api/client";
import type {
  ActorRow,
  BulkVerifyOut,
  CampaignList as CampaignListResponse,
  FarmObligationDetail,
  MassifList,
  ObligationContourOut,
  VerificationQueue as VerificationQueueResponse,
  VerificationQueueRowOut,
} from "../../api/client";
import { useRequest } from "../../api/useRequest";
import { useAuth } from "../../auth/context";
import { layout, PageHeader } from "../../components/AppShell";
import { districtStep, LevelTrail } from "../../components/LevelTrail";
import { ComboBox } from "../../components/ComboBox";
import { Select, type ChoiceOption } from "../../components/Select";
import { TextField } from "../../components/TextField";
import {
  AreaProgress,
  DoneSplit,
  ProgressRow,
  progressStyles,
} from "../../components/Progress";
import {
  Area,
  Badge,
  Button,
  Card,
  Count,
  DataStatePanel,
  EvidenceBadge,
  InlineAlert,
  RequestErrorPanel,
  ScopeNotice,
} from "../../components/ui";
import { dateLong, windowLabel, WORDS } from "../../astra/lib/format";
import { COMMON } from "../../i18n/common";
import { defineMessages } from "../../i18n/define";
import { useT } from "../../i18n/hooks";
import { tr } from "../../i18n/runtime";
import { nounForms } from "../actors/actorModel";
import { levelPath } from "../../lib/levels";
import { denominatorsOf } from "../../lib/progress";
import features from "../features.module.css";
import { SignInForm } from "../works/MyWorks";
import { deadlineView, progressLabel, stateView } from "../works/worksModel";
import styles from "./verification.module.css";
import {
  bulkVerifyNote,
  canSubmit,
  claimPair,
  groupByCampaign,
  needsReason,
  openWindowNote,
  outcomeHint,
  outcomeLabel,
  outcomePast,
  OUTCOMES,
  queueAnchor,
  queueSplit,
  type QueueGroup,
  roleView,
} from "./verificationModel";

const CAMPAIGN_PARAM = "campaign_id";
const ACTOR_PARAM = "actor_id";
const MASSIF_PARAM = "massif_id";
const DEADLINE_PARAM = "deadline";
/**
 * Два сужения, у которых НЕТ своего контрола: их ставит только переход с
 * карточки контура или из строки работы. Контрол здесь был бы третьим
 * справочником, который никто не листает; а вот сказать вслух, что выборка
 * сужена, экран обязан — иначе знаменатели прочитаются как районные.
 */
const CONTOUR_PARAM = "contour_id";
const OBLIGATION_PARAM = "farm_obligation_id";

/**
 * Сколько работ показывать за раз. Очередь читают за ответом «куда ехать
 * первым», а не целиком: сервер уже отсортировал её по вниманию, и первые
 * пятьдесят строк — это и есть ответ.
 */
const QUEUE_LIMIT = 50;

/** Сколько контуров раскрывать сразу: у крупного хозяйства их сотни. */
const CONTOURS_SHOWN = 40;

/**
 * Значения фильтра по сроку. «Не просрочено» намеренно не названо «срок идёт»:
 * под него попадает и закрытое окно с полным отчётом, и подписать его «идёт»
 * значило бы сказать неправду про календарь. «Срока нет» — третье значение, а
 * не частный случай второго.
 */
const m = defineMessages({
  title: { id: 'canonVerification.screen.title', defaultMessage: 'Проверка', description: 'Заголовок экрана проверки (инспектор, менеджер) и звено цепочки уровней' },
  purpose: { id: 'canonVerification.screen.purpose', defaultMessage: 'Работы, о которых отчитались и которые ещё не разобрал проверяющий. Проверка не меняет отчёт: при расхождении обе записи остаются, и состояние показывает конфликт, а не «опровергнуто».', description: 'Пояснение под заголовком экрана проверки' },
  signInTitle: { id: 'canonVerification.signIn.title', defaultMessage: 'Нужен вход', description: 'Заголовок панели для невошедшего пользователя на экране проверки' },
  signInText: { id: 'canonVerification.signIn.text', defaultMessage: 'Очередь видна всем — чтение в пилоте открыто. Вносить суждение может только проверяющий или менеджер хокимията: войдите, чтобы работать.', description: 'Пояснение в панели «Нужен вход» на экране проверки' },
  cannotTitle: { id: 'canonVerification.cannot.title', defaultMessage: 'Проверять нельзя', description: 'Заголовок предупреждения: у вошедшего аккаунта нет права проверять' },
  cannotText: { id: 'canonVerification.cannot.text', defaultMessage: 'У аккаунта нет роли проверяющего или менеджера. Очередь читать можно — записать суждение нельзя, и спрятанные кнопки здесь ничего не решают: отказ приходит с сервера.', description: 'Пояснение в предупреждении «Проверять нельзя»' },
  managerTitle: { id: 'canonVerification.manager.title', defaultMessage: 'Вы действуете как менеджер хокимията', description: 'Заголовок предупреждения для менеджера хокимията, вносящего проверку' },
  managerText: { id: 'canonVerification.manager.text', defaultMessage: 'Роль записывается в саму проверку и остаётся в ней навсегда: «проверил менеджер» и «проверил проверяющий» — разные утверждения о независимости. Менеджер бывает автором проверяемого отчёта, и экран обязан это показать, а не растворить в общем слове «проверено».', description: 'Пояснение в предупреждении для менеджера хокимията' },
  all: { id: 'canonVerification.filter.all', defaultMessage: 'все', description: 'Вариант фильтра «не применён» в списках «Кампания» и «Массив»; коротко' },
  filterCampaign: { id: 'canonVerification.filter.campaign', defaultMessage: 'Кампания', description: 'Подпись списка-фильтра очереди проверки: кампания' },
  filterMassif: { id: 'canonVerification.filter.massif', defaultMessage: 'Массив', description: 'Подпись списка-фильтра очереди проверки: массив' },
  filterDeadline: { id: 'canonVerification.filter.deadline', defaultMessage: 'Срок', description: 'Подпись списка-фильтра очереди проверки: срок работы' },
  deadlineAny: { id: 'canonVerification.filter.deadline.any', defaultMessage: 'любой', description: 'Вариант фильтра «Срок»: без отбора' },
  deadlineOverdue: { id: 'canonVerification.filter.deadline.overdue', defaultMessage: 'просрочено', description: 'Вариант фильтра «Срок»: срок прошёл' },
  deadlineRunning: { id: 'canonVerification.filter.deadline.running', defaultMessage: 'не просрочено', description: 'Вариант фильтра «Срок»: срок не прошёл (в том числе закрытое окно с полным отчётом)' },
  deadlineNone: { id: 'canonVerification.filter.deadline.none', defaultMessage: 'срока нет', description: 'Вариант фильтра «Срок»: у работы нет срока' },
  deadlineExplain: { id: 'canonVerification.filter.deadline.explain', defaultMessage: '«Не просрочено» — не «срок идёт»: под него попадает и закрытое окно с полным отчётом. «Срока нет» — третье значение, а не частный случай второго.', description: 'Подсказка (значок «?») у фильтра «Срок»' },
  filterQueue: { id: 'canonVerification.filter.queue', defaultMessage: 'Очередь', description: 'Подпись списка-фильтра: какие работы показывать в очереди' },
  viewAwaiting: { id: 'canonVerification.filter.queue.awaiting', defaultMessage: 'ждут суждения', description: 'Вариант списка «Очередь»: только работы, ожидающие суждения' },
  viewAwaitingHint: { id: 'canonVerification.filter.queue.awaitingHint', defaultMessage: 'по умолчанию', description: 'Короткая подпись справа от варианта «ждут суждения»' },
  viewAll: { id: 'canonVerification.filter.queue.all', defaultMessage: 'все, включая разобранные', description: 'Вариант списка «Очередь»: и разобранные работы тоже' },
  queueExplain: { id: 'canonVerification.filter.queue.explain', defaultMessage: 'Разобранная работа не исчезает из корпуса — она перестаёт ждать суждения. Вид «все» показывает и её: числа выше при этом не меняются, они считаются по всей отобранной работе, а не по очереди.', description: 'Подсказка (значок «?») у списка «Очередь»' },
  clearActor: { id: 'canonVerification.filter.clearActor', defaultMessage: 'Снять фильтр по хозяйству', description: 'Кнопка в панели фильтров: убрать отбор по хозяйству' },
  massifHint: { id: 'canonVerification.hint.massif', defaultMessage: 'Массив выбран: числа ниже посчитаны ТОЛЬКО по нему. Районные итоги живут на экране кампаний.', description: 'Пояснение под фильтрами, когда выбран массив' },
  oneWork: { id: 'canonVerification.hint.oneWork', defaultMessage: 'Показана ОДНА работа (№ {id}). Все числа выше — про неё.', description: 'Пояснение, когда очередь сужена до одной работы на поле; {id} — номер работы' },
  oneContour: { id: 'canonVerification.hint.oneContour', defaultMessage: 'Показаны работы одного контура (№ {id}). Все числа выше посчитаны по нему, а не по хозяйству и не по району.', description: 'Пояснение, когда очередь сужена до одного контура; {id} — номер контура' },
  showWholeQueue: { id: 'canonVerification.hint.showWholeQueue', defaultMessage: 'Показать всю очередь', description: 'Кнопка рядом с пояснением о сужении очереди: снять сужение' },
  queueFailed: { id: 'canonVerification.queue.failed', defaultMessage: 'Очередь не загрузилась', description: 'Заголовок панели ошибки загрузки очереди проверки' },
  queueLoading: { id: 'canonVerification.queue.loading', defaultMessage: 'Загрузка очереди', description: 'Заголовок заглушки загрузки очереди проверки' },
  stateBucket: { id: 'canonVerification.queue.stateBucket', defaultMessage: '{label} — {count}', description: 'Элемент списка состояний: название состояния и число работ' },
  states: { id: 'canonVerification.queue.states', defaultMessage: 'Состояния отобранной работы: {states}. «Данных нет» — это отсутствие отчёта, а не невыполнение.', description: 'Строка над очередью; {states} — список состояний через «; »' },
  verdictRecorded: { id: 'canonVerification.verdict.title', defaultMessage: 'Записано: {work}', description: 'Заголовок подтверждения после записи суждения; {work} — название работы' },
  verdictText: { id: 'canonVerification.verdict.text', defaultMessage: '{note} Суждение внесено в качестве «{role}».', description: 'Текст подтверждения после записи суждения; {note} — итог записи, {role} — роль вносившего' },
  emptyTitle: { id: 'canonVerification.queue.emptyTitle', defaultMessage: 'Очередь пуста', description: 'Заголовок пустого состояния очереди проверки' },
  shownOf: { id: 'canonVerification.queue.shownOf', defaultMessage: 'Показано {shown} из {total, plural, one {# работы} few {# работ} many {# работ} other {# работ}}. Очередь разложена по кампаниям — по ним и строится выезд. Внутри кампании порядок сервера сохранён: сверху те, куда ехать первым — просрочка, затем ближний срок, затем объём ожидающей работы. Кампании между собой не ранжированы.', description: 'Пояснение над очередью проверки; {shown} — сколько строк показано, {total} — сколько всего' },
  noReportNote: { id: 'canonVerification.queue.noReportNote', defaultMessage: '«Без отчёта» — это отсутствие данных, а не невыполнение: такие контуры не ждут проверяющего, потому что проверять нечего. У работы с ещё не закрывшимся окном отсутствие отчёта означает «ещё не срок».', description: 'Общее пояснение под очередью (одно на экран) про «Без отчёта»' },
  groupHead: { id: 'canonVerification.group.head', defaultMessage: '{rows, plural, one {# работа} few {# работы} many {# работ} other {# работы}} в очереди · ждут суждения: {awaiting}', description: 'Мелкая подпись у заголовка кампании в очереди; {rows} — работ в группе, {awaiting} — сколько контуров ждёт суждения' },
  groupHeadTruncated: { id: 'canonVerification.group.headTruncated', defaultMessage: '{rows, plural, one {# работа} few {# работы} many {# работ} other {# работы}} в очереди · ждут суждения: {awaiting} · по показанным строкам этой кампании', description: 'Мелкая подпись у заголовка кампании, когда очередь показана не целиком' },
  groupTruncated: { id: 'canonVerification.group.truncated', defaultMessage: 'Развилка выше сложена по показанным строкам этой кампании, а не по всей кампании: очередь пришла одной страницей. Ход кампании целиком — на её собственном экране.', description: 'Пояснение под заголовком кампании: числа посчитаны по показанным строкам' },
  actorUnnamed: { id: 'canonVerification.card.actorUnnamed', defaultMessage: 'хозяйство #{id}', description: 'Заголовок карточки работы, когда у хозяйства нет имени; {id} — номер записи' },
  workBadgeTitle: { id: 'canonVerification.card.workBadgeTitle', defaultMessage: 'Работа, которую ставит кампания', description: 'Подсказка по наведению на бейдж с названием работы' },
  window: { id: 'canonVerification.card.window', defaultMessage: 'окно {window}', description: 'Мелкая подпись окна работы; {window} — окно дат, например «1 апр — 15 мая 2026»' },
  toWork: { id: 'canonVerification.card.toWork', defaultMessage: 'Уровень работы', description: 'Ссылка в карточке очереди: открыть экран самой работы на поле' },
  awaitingLabel: { id: 'canonVerification.card.awaitingLabel', defaultMessage: 'Ждут суждения:', description: 'Подпись числа в карточке: контуры, ожидающие суждения проверяющего' },
  decidedLabel: { id: 'canonVerification.card.decidedLabel', defaultMessage: 'Уже разобрано:', description: 'Подпись числа в карточке: контуры, по которым суждение уже вынесено' },
  noReportLabel: { id: 'canonVerification.card.noReportLabel', defaultMessage: 'Без отчёта:', description: 'Подпись числа в карточке: контуры без отчёта (это отсутствие данных)' },
  areaLine: { id: 'canonVerification.card.area', defaultMessage: 'Площадь: <reported></reported> из <total></total>', description: 'Подпись площади в карточке работы; <reported> — площадь с отчётом (число без единицы), <total> — вся площадь работы (число с единицей)' },
  notDue: { id: 'canonVerification.card.notDue', defaultMessage: '<badge>нет отчёта — ещё не срок</badge> окно не закрылось: с этих {count, plural, one {# контура} few {# контуров} many {# контуров} other {# контуров}} спрашивать пока не с чего.', description: 'Пояснение в карточке работы с незакрытым окном; <badge> — бейдж; {count} — контуры без отчёта' },
  onBehalf: { id: 'canonVerification.card.onBehalf', defaultMessage: 'Отчёт внесён ОТ ИМЕНИ хозяйства. Заявление сделал не сам адресат работы — это стоит знать до того, как вынести суждение.', description: 'Предупреждение в карточке: отчёт внёс не хозяин, а менеджер за хозяйство' },
  onBehalfWho: { id: 'canonVerification.card.onBehalfWho', defaultMessage: 'Отчёт внесён ОТ ИМЕНИ хозяйства: {who}. Заявление сделал не сам адресат работы — это стоит знать до того, как вынести суждение.', description: 'Предупреждение в карточке: отчёт внёс менеджер за хозяйство; {who} — кто внёс' },
  lastVerdict: { id: 'canonVerification.card.lastVerdict', defaultMessage: 'Последнее суждение: {label}{hasBy, select, yes {, {by}} other {}} ({role}){hasDate, select, yes {, {date}} other {}}.', description: 'Мелкая строка в карточке; {label} — исход, {by} — кто вынес (если известно), {role} — его роль, {date} — дата (если известна)' },
  scopeWhole: { id: 'canonVerification.form.scopeWhole', defaultMessage: 'по всему хозяйству ({count, plural, one {# контур ждёт} few {# контура ждут} many {# контуров ждут} other {# контура ждут}})', description: 'Уточнение к подписи формы суждения: суждение по всем контурам работы; {count} — сколько контуров ждёт' },
  scopeContour: { id: 'canonVerification.form.scopeContour', defaultMessage: 'по этому контуру', description: 'Уточнение к подписи формы суждения: суждение по одному контуру' },
  verdictLabel: { id: 'canonVerification.form.label', defaultMessage: 'Суждение {scope}', description: 'Подпись над формой суждения; {scope} — «по всему хозяйству (…)» или «по этому контуру»' },
  outcomeAria: { id: 'canonVerification.form.outcomeAria', defaultMessage: 'Исход проверки', description: 'Подпись для скринридера у списка исходов проверки' },
  reasonRequired: { id: 'canonVerification.form.reasonRequired', defaultMessage: 'Причина (обязательна)', description: 'Подпись поля причины, когда выбранный исход требует причину' },
  reasonOptional: { id: 'canonVerification.form.reasonOptional', defaultMessage: 'Причина (необязательна)', description: 'Подпись поля причины, когда причина не требуется' },
  reasonPlaceholderRequired: { id: 'canonVerification.form.reasonPlaceholderRequired', defaultMessage: 'Что именно не сошлось', description: 'Подсказка внутри обязательного поля причины' },
  reasonPlaceholderOptional: { id: 'canonVerification.form.reasonPlaceholderOptional', defaultMessage: 'Можно оставить пустым', description: 'Подсказка внутри необязательного поля причины' },
  reasonExplain: { id: 'canonVerification.form.reasonExplain', defaultMessage: 'Причина остаётся в суждении навсегда и читается на разборе вместе с ним. «Не сходится» без причины — это не суждение, а его отсутствие: следующий человек не узнает, что именно проверять заново.', description: 'Подсказка (значок «?») у поля причины суждения' },
  save: { id: 'canonVerification.form.save', defaultMessage: 'Записать', description: 'Кнопка формы суждения: записать суждение; коротко' },
  notAccepted: { id: 'canonVerification.form.notAccepted', defaultMessage: 'Суждение не принято', description: 'Заголовок предупреждения об отказе сервера принять суждение; ниже текст отказа' },
  hideContours: { id: 'canonVerification.card.hideContours', defaultMessage: 'Свернуть контуры', description: 'Кнопка в карточке работы: свернуть список контуров' },
  showContours: { id: 'canonVerification.card.showContours', defaultMessage: 'Показать контуры', description: 'Кнопка в карточке работы: раскрыть список контуров' },
  toFarm: { id: 'canonVerification.card.toFarm', defaultMessage: 'Экран хозяйства', description: 'Ссылка в карточке работы: открыть экран работ этого хозяйства' },
  actorLabel: { id: 'canonVerification.actor.label', defaultMessage: 'Хозяйство', description: 'Подпись поля поиска хозяйства в фильтрах очереди проверки' },
  actorPlaceholder: { id: 'canonVerification.actor.placeholder', defaultMessage: 'часть названия', description: 'Подсказка внутри поля поиска хозяйства' },
  actorSearchFailed: { id: 'canonVerification.actor.searchFailed', defaultMessage: 'Поиск не ответил. Повторите запрос.', description: 'Пустое состояние списка подсказок хозяйств при ошибке запроса' },
  actorNothing: { id: 'canonVerification.actor.nothing', defaultMessage: 'Ничего не нашли. Имя ищется по части названия.', description: 'Пустое состояние списка подсказок хозяйств: ничего не найдено' },
  actorMore: { id: 'canonVerification.actor.more', defaultMessage: 'ещё {count, plural, one {# совпадение} few {# совпадения} many {# совпадений} other {# совпадения}} — уточните запрос', description: 'Подпись под списком подсказок хозяйств; {count} — сколько совпадений скрыто' },
  actorExplain: { id: 'canonVerification.actor.explain', defaultMessage: 'Ищет сервер по части названия и в своём порядке — первая строка не «самая подходящая». Хозяйство в этом срезе — кластер нормализованного имени, а не юридическое лицо.', description: 'Подсказка (значок «?») у поля поиска хозяйства' },
  contoursFailed: { id: 'canonVerification.contours.failed', defaultMessage: 'Контуры не загрузились', description: 'Заголовок панели ошибки загрузки контуров работы' },
  contoursLoading: { id: 'canonVerification.contours.loading', defaultMessage: 'Загрузка контуров', description: 'Заголовок заглушки загрузки контуров работы' },
  contoursIntro: { id: 'canonVerification.contours.intro', defaultMessage: 'Контуры этой работы. У каждого — что сказало хозяйство и что сказал проверяющий, рядом и без «итога»: ни одно утверждение не отменяет другое, и обе записи остаются в базе.', description: 'Пояснение над списком контуров раскрытой работы' },
  contoursShown: { id: 'canonVerification.contours.shown', defaultMessage: 'Показано {shown} из {total, plural, one {# контура} few {# контуров} many {# контуров} other {# контуров}}.', description: 'Строка под списком контуров; {shown} — сколько показано, {total} — всего' },
  showAll: { id: 'canonVerification.contours.showAll', defaultMessage: 'Показать все', description: 'Кнопка под списком контуров: раскрыть все контуры' },
  contourTitle: { id: 'canonVerification.claim.title', defaultMessage: 'Контур {no}', description: 'Заголовок карточки контура в раскрытой работе; {no} — номер контура' },
  contourTitlePart: { id: 'canonVerification.claim.titlePart', defaultMessage: 'Контур {no} · часть #{part}', description: 'Заголовок карточки контура, когда работа идёт по части контура; {part} — номер части' },
  awaitingBadge: { id: 'canonVerification.claim.awaitingBadge', defaultMessage: 'ждёт проверки', description: 'Бейдж у контура: отчёт есть, суждения нет; коротко' },
  awaitingBadgeTitle: { id: 'canonVerification.claim.awaitingBadgeTitle', defaultMessage: 'Отчёт есть, суждения нет', description: 'Подсказка по наведению на бейдж «ждёт проверки»' },
  claimFarm: { id: 'canonVerification.claim.farmTitle', defaultMessage: 'Что сказало хозяйство', description: 'Заголовок левой колонки спора: заявление хозяйства (колонки равной ширины)' },
  claimNoReport: { id: 'canonVerification.claim.noReport', defaultMessage: 'Отчёта нет. Это отсутствие данных, а не «не выполнено», — и проверять здесь нечего.', description: 'Текст в колонке «Что сказало хозяйство», когда отчёта нет' },
  claimProgressDate: { id: 'canonVerification.claim.progressDate', defaultMessage: '{progress}, {date}', description: 'Что и когда отчитало хозяйство; {progress} — выполненный объём словами, {date} — дата отчёта' },
  claimBy: { id: 'canonVerification.claim.by', defaultMessage: 'внёс: {who}', description: 'Мелкая подпись: кто внёс отчёт; {who} — имя' },
  claimByBehalf: { id: 'canonVerification.claim.byBehalf', defaultMessage: 'внёс: {who} · от имени хозяйства', description: 'Мелкая подпись: отчёт внесён за хозяйство; {who} — кто внёс' },
  authorUnknown: { id: 'canonVerification.claim.authorUnknown', defaultMessage: 'автор не указан', description: 'Подстановка вместо имени автора записи, когда автор не указан' },
  claimArea: { id: 'canonVerification.claim.area', defaultMessage: 'площадь: <area></area>', description: 'Мелкая подпись в колонке хозяйства: площадь контура; <area> — число с единицей' },
  claimVerifier: { id: 'canonVerification.claim.verifierTitle', defaultMessage: 'Что сказал проверяющий', description: 'Заголовок правой колонки спора: суждение проверяющего' },
  claimWaiting: { id: 'canonVerification.claim.waiting', defaultMessage: 'Суждения ещё нет — работа ждёт проверки.', description: 'Текст в колонке «Что сказал проверяющий»: отчёт есть, суждения нет' },
  claimNone: { id: 'canonVerification.claim.none', defaultMessage: 'Суждения нет.', description: 'Текст в колонке «Что сказал проверяющий»: суждения нет, проверять нечего' },
  claimVerifierBulk: { id: 'canonVerification.claim.verifierBulk', defaultMessage: 'внёс: {who} ({role}) · одним выездом по хозяйству', description: 'Мелкая подпись суждения: кто внёс, его роль; суждение внесено сразу на всё хозяйство' },
  claimVerifierOne: { id: 'canonVerification.claim.verifierOne', defaultMessage: 'внёс: {who} ({role}) · поштучно', description: 'Мелкая подпись суждения: кто внёс, его роль; суждение внесено по этому контуру отдельно' },
  claimNote: { id: 'canonVerification.claim.note', defaultMessage: 'причина: {note}', description: 'Мелкая подпись суждения: причина, которую назвал проверяющий' },
  disputedTitle: { id: 'canonVerification.claim.disputedTitle', defaultMessage: 'Спор не решён', description: 'Заголовок предупреждения: хозяйство заявило выполнение, проверяющий опроверг' },
  disputedText: { id: 'canonVerification.claim.disputedText', defaultMessage: 'Хозяйство заявило выполнение, проверяющий это опроверг. Обе записи остаются в базе, и состояние работы на поле — «конфликт», а не «опровергнуто»: система не делает вид, что спор разрешён.', description: 'Текст предупреждения «Спор не решён»' },
  nothingToCheck: { id: 'canonVerification.claim.nothingToCheck', defaultMessage: 'Проверять нечего: отчёта по контуру нет. Отсутствие отчёта не является утверждением «не сделано», и записать опровержение здесь нельзя.', description: 'Пояснение вместо формы суждения у контура без отчёта' },
});

/** Фильтр по сроку: подписи берутся при построении, на языке интерфейса. */
const deadlineOptions = (): ReadonlyArray<ChoiceOption> => [
  { value: "any", label: tr(m.deadlineAny) },
  { value: "overdue", label: tr(m.deadlineOverdue) },
  { value: "running", label: tr(m.deadlineRunning) },
  { value: "none", label: tr(m.deadlineNone) },
];

/**
 * Что показывать в очереди. Это ВИД, а не фильтр качества: разобранная работа
 * не исчезает из корпуса, она просто перестаёт ждать. Флажок этого не говорил
 * — он говорил «показывать», как будто вторая половина чем-то хуже.
 */
const queueViews = (): ReadonlyArray<ChoiceOption> => [
  { value: "awaiting", label: tr(m.viewAwaiting), hint: tr(m.viewAwaitingHint) },
  { value: "all", label: tr(m.viewAll) },
];

/** Исходы проверки в том порядке и с теми подписями, что у сервера. */
const outcomeOptions = (): ReadonlyArray<ChoiceOption> =>
  OUTCOMES.map((value) => ({
    value,
    label: outcomeLabel(value).label,
  }));

/** Одна строка с одного запроса не ищется: справочник хозяйств велик. */
const ACTOR_MIN_QUERY = 2;
/** Пауза набора: запрос уходит за словом, а не за буквой. */
const ACTOR_DEBOUNCE_MS = 250;
/** Бюджет первого взгляда: список подсказок — не реестр. */
const ACTOR_LIMIT = 8;

export function VerificationScreen() {
  const t = useT();
  const context = useAnalysisContext();
  const auth = useAuth();
  const [search, setSearch] = useSearchParams();
  const [showDecided, setShowDecided] = useState(false);
  const [generation, setGeneration] = useState(0);
  // Итог записи живёт на уровне экрана: разобрав работу, карточка уходит из
  // очереди — и ответ сервера ушёл бы вместе с ней, оставив человека без
  // подтверждения того, что он сделал.
  const [lastVerdict, setLastVerdict] = useState<{
    work: string;
    result: BulkVerifyOut;
  } | null>(null);

  const campaignId = search.get(CAMPAIGN_PARAM);
  const actorId = search.get(ACTOR_PARAM);
  const massifId = search.get(MASSIF_PARAM);
  const deadline = search.get(DEADLINE_PARAM) ?? "any";
  const contourId = search.get(CONTOUR_PARAM);
  const farmObligationId = search.get(OBLIGATION_PARAM);

  const setParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(search);
      if (value === null || value === "") next.delete(key);
      else next.set(key, value);
      setSearch(next, { replace: true });
    },
    [search, setSearch],
  );

  const params = useMemo(
    () => ({
      ...context.params,
      limit: QUEUE_LIMIT,
      only_awaiting: !showDecided,
      deadline,
      ...(campaignId ? { campaign_id: campaignId } : {}),
      ...(actorId ? { actor_id: actorId } : {}),
      ...(massifId ? { massif_id: massifId } : {}),
      ...(contourId ? { contour_id: contourId } : {}),
      ...(farmObligationId ? { farm_obligation_id: farmObligationId } : {}),
    }),
    [
      context.params,
      showDecided,
      deadline,
      campaignId,
      actorId,
      massifId,
      contourId,
      farmObligationId,
    ],
  );

  const identity = auth.principal?.is_authenticated
    ? (auth.principal.username ?? "")
    : "";
  const queue = useRequest<VerificationQueueResponse>(
    () => api.verificationQueue(params),
    [
      context.key,
      campaignId ?? "",
      actorId ?? "",
      massifId ?? "",
      contourId ?? "",
      farmObligationId ?? "",
      deadline,
      showDecided,
      generation,
      identity,
    ],
  );
  const reload = useCallback(() => setGeneration((count) => count + 1), []);

  const campaigns = useRequest<CampaignListResponse>(
    () => api.campaigns({ ...context.params, limit: 50 }),
    [context.key],
  );
  const massifs = useRequest<MassifList>(
    () => api.massifs(context.params),
    [context.key],
  );

  // «Все» — первое значение и пустая строка: фильтр без состояния «не
  // применён» сужает очередь молча, и человек читает часть как целое.
  const campaignOptions = useMemo<ReadonlyArray<ChoiceOption>>(
    () => [
      { value: "", label: t(m.all) },
      ...(campaigns.data?.items ?? []).map((row) => ({
        value: String(row.id),
        label: row.title,
      })),
    ],
    [campaigns.data, t],
  );
  const massifOptions = useMemo<ReadonlyArray<ChoiceOption>>(
    () => [
      { value: "", label: t(m.all) },
      ...(massifs.data?.items ?? []).map((row) => ({
        value: String(row.id),
        label: row.canonical_name,
      })),
    ],
    [massifs.data, t],
  );

  const data = queue.data;
  // Разрез очереди по кампаниям: маршрут разбора. Порядок сервера внутри
  // группы сохранён — ранжировать работы заново экран права не имеет.
  const groups = useMemo(() => groupByCampaign(data?.items ?? []), [data]);
  // Очередь приходит одной страницей (`QUEUE_LIMIT`), пагинации нет. Значит
  // суммы группы сложены по ПРИШЕДШИМ строкам, и когда страница — не вся
  // выборка, развилка группы перестаёт быть ходом кампании. Первый взгляд
  // рядом считается сервером по всему отбору; разницу между двумя способами
  // счёта на одном экране обязана называть подпись, а не догадка читателя.
  const queueTruncated = data ? data.items.length < data.page.total : false;
  const principal = data?.principal ?? auth.principal;
  const canVerify = principal?.can_verify ?? false;
  const actingRole = principal?.roles.includes("verifier")
    ? "verifier"
    : principal?.roles.includes("hokimiyat_manager")
      ? "hokimiyat_manager"
      : null;
  const acting = roleView(actingRole);

  return (
    <div className={features.stack}>
      <PageHeader
        title={t(m.title)}
        breadcrumb={
          <LevelTrail
            steps={[districtStep(context.linkSearch())]}
            screen={t(m.title)}
          />
        }
        purpose={t(m.purpose)}
      />

      <ScopeNotice scope={data?.context.scope} />

      {principal && !principal.is_authenticated ? (
        <DataStatePanel state="blocked" title={t(m.signInTitle)} action={<SignInForm />}>
          {t(m.signInText)}
        </DataStatePanel>
      ) : null}

      {principal?.is_authenticated && !canVerify ? (
        <InlineAlert title={t(m.cannotTitle)}>{t(m.cannotText)}</InlineAlert>
      ) : null}

      {canVerify && acting.actingAsManager ? (
        <InlineAlert title={t(m.managerTitle)}>{t(m.managerText)}</InlineAlert>
      ) : null}

      <div className={layout.filterBar}>
        <Select
          label={t(m.filterCampaign)}
          value={campaignId ?? ""}
          options={campaignOptions}
          onChange={(value) => setParam(CAMPAIGN_PARAM, value)}
        />

        <Select
          label={t(m.filterMassif)}
          value={massifId ?? ""}
          options={massifOptions}
          onChange={(value) => setParam(MASSIF_PARAM, value)}
        />

        <Select
          label={t(m.filterDeadline)}
          value={deadline}
          options={deadlineOptions()}
          onChange={(value) => setParam(DEADLINE_PARAM, value)}
          explain={t(m.deadlineExplain)}
        />

        {/* Хозяйство — длинный справочник: в районе их сотни, и читать их
            списком незачем. До этого фильтр можно было только СНЯТЬ — задать
            его было негде, и человек, приехавший сюда с карточки хозяйства,
            обратно в него не возвращался. */}
        <ActorFilter
          value={actorId}
          onChange={(value) => setParam(ACTOR_PARAM, value)}
        />

        <Select
          label={t(m.filterQueue)}
          value={showDecided ? "all" : "awaiting"}
          options={queueViews()}
          onChange={(value) => setShowDecided(value === "all")}
          explain={t(m.queueExplain)}
        />

        {actorId ? (
          <Button onClick={() => setParam(ACTOR_PARAM, null)}>
            {t(m.clearActor)}
          </Button>
        ) : null}
      </div>

      {massifId ? (
        <p className={styles.hint}>
          {t(m.massifHint)}
        </p>
      ) : null}

      {/* Сужения без контрола. Сервер применяет их к строкам И к
          знаменателям, поэтому состояния ниже — это состояния ЭТОГО
          контура (или этой работы), а не района. Молчать об этом нельзя:
          часть, прочитанная как целое, — самая дорогая ошибка экрана. */}
      {farmObligationId ? (
        <p className={styles.hint}>
          {t(m.oneWork, { id: farmObligationId })}{" "}
          <Button onClick={() => setParam(OBLIGATION_PARAM, null)}>
            {t(m.showWholeQueue)}
          </Button>
        </p>
      ) : contourId ? (
        <p className={styles.hint}>
          {t(m.oneContour, { id: contourId })}{" "}
          <Button onClick={() => setParam(CONTOUR_PARAM, null)}>
            {t(m.showWholeQueue)}
          </Button>
        </p>
      ) : null}

      {queue.error && !data ? (
        <RequestErrorPanel
          error={queue.error}
          title={t(m.queueFailed)}
          action={<Button onClick={queue.reload}>{t(COMMON.retry)}</Button>}
        />
      ) : null}

      {!data && !queue.error ? (
        <DataStatePanel state="loading" title={t(m.queueLoading)} />
      ) : null}

      {data ? (
        <>
          {/* Первый взгляд без KPI «ждут проверки» и долей «проверено»:
              система учётная, отчёт принят как факт, проверка —
              необязательная отметка поверх (журнал решений №28). Экран —
              инструмент проверяющего, а не счётчик долга. */}
          {data.by_state.length > 0 ? (
            <p className={styles.hint}>
              {t(m.states, {
                states: data.by_state
                  .map((bucket) =>
                    t(m.stateBucket, {
                      label: stateView(bucket.key).label,
                      count: bucket.count,
                    }),
                  )
                  .join("; "),
              })}
            </p>
          ) : null}

          {lastVerdict ? (
            <InlineAlert title={t(m.verdictRecorded, { work: lastVerdict.work })}>
              {t(m.verdictText, {
                note: bulkVerifyNote(lastVerdict.result),
                role: roleView(lastVerdict.result.verifier_role).label,
              })}
            </InlineAlert>
          ) : null}

          {data.items.length === 0 ? (
            <DataStatePanel state="empty" title={t(m.emptyTitle)}>
              {data.empty_note}
            </DataStatePanel>
          ) : (
            <>
              <p className={styles.hint}>
                {t(m.shownOf, { shown: data.items.length, total: data.page.total })}
              </p>
              {/* Это объяснение стоит ОДИН раз на экран. Тот же абзац,
                  напечатанный в каждой из пятидесяти карточек, перестают
                  читать после второй — и вместе с ним перестают читать
                  карточку. */}
              <p className={styles.hint}>
                {t(m.noReportNote)}
              </p>
            </>
          )}

          {groups.map((group) => (
            <QueueGroupSection
              key={group.campaignId}
              group={group}
              truncated={queueTruncated}
              canVerify={canVerify}
              onChanged={reload}
              onVerified={setLastVerdict}
            />
          ))}
        </>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Кампания как разрез очереди                                         */
/* ------------------------------------------------------------------ */

/**
 * Группа очереди — это МАРШРУТ, а не рейтинг.
 *
 * Выезд планируется по одной работе: проверяющий едет смотреть полив, а не
 * «десять разных работ у десяти хозяйств». Поэтому у группы стоит своя
 * двухосевая развилка со своим знаменателем и своё число ожидания. Знаменатель
 * этот — обязательства работ ЭТОЙ кампании, ПОПАВШИХ на страницу очереди: она
 * приходит одним куском без пагинации. Пока страница — вся выборка, это и есть
 * ход кампании в отборе; как только очередь обрезана, подпись обязана сказать,
 * что числа страничные (`truncated`). Сравнивать группы между собой экран не
 * предлагает: оценки кампаний в корпусе нет.
 */
function QueueGroupSection({
  group,
  truncated,
  canVerify,
  onChanged,
  onVerified,
}: {
  group: QueueGroup;
  /** Очередь показана не целиком: суммы группы — по показанным строкам. */
  truncated: boolean;
  canVerify: boolean;
  onChanged: () => void;
  onVerified: (entry: { work: string; result: BulkVerifyOut }) => void;
}) {
  const t = useT();
  const note = openWindowNote(group);

  return (
    <section className={features.stack}>
      <div className={styles.groupHead}>
        <div className={styles.groupTitle}>
          {group.campaignTitle}
          <span className={styles.hint}>
            {truncated
              ? t(m.groupHeadTruncated, { rows: group.rows.length, awaiting: group.awaiting })
              : t(m.groupHead, { rows: group.rows.length, awaiting: group.awaiting })}
          </span>
        </div>
        <DoneSplit counts={group.obligations} noun={nounForms(WORDS.obligation)} />
      </div>
      {truncated ? (
        <p className={styles.hint}>{t(m.groupTruncated)}</p>
      ) : null}
      {note ? <p className={styles.hint}>{note}</p> : null}

      {group.rows.map((row) => (
        // Якорь строки. У пятого уровня теперь есть и собственный адрес
        // (`/works/:id`), но якорь остаётся: ссылка «Проверить» с экрана
        // кампании ведёт в очередь, к месту СУЖДЕНИЯ, а не на карточку
        // просмотра — это разные жесты.
        <div key={row.farm_obligation_id} id={queueAnchor(row.farm_obligation_id)}>
          <QueueCard
            row={row}
            canVerify={canVerify}
            onChanged={onChanged}
            onVerified={(result) => onVerified({ work: row.work_name, result })}
          />
        </div>
      ))}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Карточка одной работы в очереди                                     */
/* ------------------------------------------------------------------ */

function QueueCard({
  row,
  canVerify,
  onChanged,
  onVerified,
}: {
  row: VerificationQueueRowOut;
  canVerify: boolean;
  onChanged: () => void;
  onVerified: (result: BulkVerifyOut) => void;
}) {
  const t = useT();
  const context = useAnalysisContext();
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const state = stateView(row.state);
  const deadline = deadlineView({
    overdue: row.overdue,
    window_to: row.window_to,
    window_elapsed: row.window_elapsed,
  });
  const split = queueSplit(row);
  const last = outcomePast(row.last_verification_outcome);

  const submit = async (outcome: string, reason: string) => {
    setBusy(true);
    setError(null);
    try {
      const answer = await api.verifyFarmObligation(
        row.farm_obligation_id,
        { outcome, note: reason, occurred_on: today() },
        context.params,
      );
      onVerified(answer);
      onChanged();
    } catch (cause) {
      setError(cause as ApiError);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title={
        <>
          {row.actor.name ?? t(m.actorUnnamed, { id: row.actor.id })}{" "}
          <EvidenceBadge status={row.epistemic_status} />
        </>
      }
      large
    >
      {/* Кампания здесь не повторяется: она — заголовок группы, в которой эта
          карточка стоит. Повтор занимал место и ничего не различал. */}
      <div className={features.rowWrap}>
        <Badge title={t(m.workBadgeTitle)}>{row.work_name}</Badge>
        <Badge tone={state.attention ? "attention" : "neutral"}>{state.label}</Badge>
        <Badge tone={deadline.overdue ? "attention" : "neutral"}>
          {deadline.label}
        </Badge>
        <span className={styles.hint}>
          {t(m.window, { window: windowLabel(row.window_from, row.window_to) })}
        </span>
        {/* Спуск на уровень работы. Очередь отвечает на вопрос «о чём заявлено
            и ещё не разобрано» и остаётся местом СУЖДЕНИЯ; уровень работы
            отвечает на вопрос «что это за работа и что по ней записано». */}
        <Link
          to={{
            pathname: levelPath("work", row.farm_obligation_id),
            search: context.linkSearch(),
          }}
        >
          {t(m.toWork)}
        </Link>
      </div>

      {state.hint ? <p className={styles.hint}>{state.hint}</p> : null}

      <div className={styles.split}>
        <span>
          {t(m.awaitingLabel)} <Count value={split.awaiting} />
        </span>
        <span>
          {t(m.decidedLabel)} <Count value={split.decided} />
        </span>
        <span>
          {t(m.noReportLabel)} <Count value={split.noReport} />
        </span>
        <span>
          {t(m.areaLine, {
            reported: () => <Area microha={row.area.reported_microha} unit={null} />,
            total: () => <Area microha={row.area.total_microha} />,
          })}
        </span>
      </div>
      {/* Слово вместо абзаца: отсутствие отчёта у ещё не закрывшегося окна
          означает календарь, а не невыполнение. Общее объяснение стоит один
          раз на экране — здесь остаётся только различение этой строки. */}
      {split.noReport > 0 && row.window_elapsed === false ? (
        <p className={styles.hint}>
          {t(m.notDue, {
            badge: (chunks) => <Badge>{chunks}</Badge>,
            count: split.noReport,
          })}
        </p>
      ) : null}

      {row.last_entry_on_behalf ? (
        <p className={styles.actingNote}>
          {row.last_entry_by
            ? t(m.onBehalfWho, { who: row.last_entry_by })
            : t(m.onBehalf)}
        </p>
      ) : null}

      {row.last_verification_outcome ? (
        <p className={styles.hint}>
          {t(m.lastVerdict, {
            label: last.label,
            hasBy: row.last_verification_by ? "yes" : "other",
            by: row.last_verification_by ?? "",
            role: roleView(row.last_verification_role).label,
            hasDate: row.last_verification_on ? "yes" : "other",
            date: dateLong(row.last_verification_on),
          })}
        </p>
      ) : null}

      {/* Форма суждения стоит СРАЗУ под величинами, ради которых её открыли:
          человек прочитал, сколько ждёт и что сказало хозяйство, — и
          высказывается. Три знаменателя ниже нужны, чтобы разобраться, а не
          чтобы дойти до кнопки. */}
      {canVerify ? (
        <VerdictForm
          busy={busy}
          scope={t(m.scopeWhole, { count: split.awaiting })}
          onSubmit={submit}
        />
      ) : null}

      {error ? (
        <InlineAlert title={t(m.notAccepted)}>{error.message}</InlineAlert>
      ) : null}

      <div className={progressStyles.progressGrid}>
        {denominatorsOf({
          obligations: row.obligations,
          contours: row.contours,
          // Знаменателя «хозяйства» на карточке одного хозяйства нет: «1 из 1»
          // изображало бы долю там, где её не существует.
          actors: { total: 0, reported: 0, verified: 0 },
        })
          .slice(0, 2)
          .map((view) => (
            <ProgressRow key={view.label} view={view} />
          ))}
        <AreaProgress area={row.area} />
      </div>

      <div className={styles.actions}>
        <Button onClick={() => setExpanded((value) => !value)}>
          {expanded ? t(m.hideContours) : t(m.showContours)}
        </Button>
        <Link
          to={{
            pathname: "/my-works",
            search: context.linkSearch({ actor_id: row.actor.id }),
          }}
        >
          {t(m.toFarm)}
        </Link>
      </div>

      {expanded ? (
        <ContourVerdicts
          farmObligationId={row.farm_obligation_id}
          canVerify={canVerify}
          onChanged={onChanged}
        />
      ) : null}
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Фильтр по хозяйству: длинный справочник                             */
/* ------------------------------------------------------------------ */

/**
 * Хозяйство выбирается набором имени, а не листанием списка: в районе их
 * сотни, и `<select>` на такую длину — это прокрутка вместо ответа.
 *
 * Два обещания держатся формой:
 *
 * * **порядок выдачи не переставляется.** Хозяйства идут так, как их отдал
 *   сервер; сортировка «по похожести» была бы оценкой похожести, которой в
 *   корпусе нет, и первая строка читалась бы как «самое подходящее»;
 * * **применённый фильтр называет себя именем.** Сюда приходят по ссылке с
 *   карточки хозяйства, где в адресе только номер. Поле, показывающее пустоту
 *   при работающем фильтре, врёт про выборку.
 */
function ActorFilter({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (next: string | null) => void;
}) {
  const t = useT();
  const context = useAnalysisContext();
  // Контекст читается в момент запроса, а не через список зависимостей:
  // пересоздание объекта провайдером не должно перезапускать набранное.
  const contextRef = useRef(context);
  contextRef.current = context;

  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<ActorRow[]>([]);
  const [hidden, setHidden] = useState(0);
  const [state, setState] = useState<"idle" | "loading" | "ready" | "error">(
    "idle",
  );
  /** Чьё имя уже стоит в поле: чтобы не запрашивать его второй раз. */
  const named = useRef<string | null>(null);

  useEffect(() => {
    if (!value) {
      named.current = null;
      return;
    }
    if (named.current === value) return;
    let cancelled = false;
    api
      .actor(Number(value), contextRef.current.params)
      .then((row) => {
        if (cancelled) return;
        named.current = value;
        setQuery(row.name ?? tr(m.actorUnnamed, { id: row.id }));
      })
      .catch(() => {
        // Имя не пришло — фильтр всё равно применён, и молчать об этом
        // нельзя: подпись под полем называет его номером.
      });
    return () => {
      cancelled = true;
    };
  }, [value]);

  const text = query.trim();
  const short = text.length < ACTOR_MIN_QUERY;

  useEffect(() => {
    if (short) {
      setHits([]);
      setHidden(0);
      setState("idle");
      return;
    }
    let cancelled = false;
    setState("loading");
    const timer = window.setTimeout(() => {
      api
        .actors({
          ...contextRef.current.params,
          search: text,
          limit: ACTOR_LIMIT,
        })
        .then((list) => {
          if (cancelled) return;
          setHits(list.items);
          // Обрезанная выдача, молчащая об остатке, читается как «больше
          // ничего нет».
          setHidden(Math.max(0, list.page.total - list.items.length));
          setState("ready");
        })
        .catch(() => {
          if (cancelled) return;
          setHits([]);
          setHidden(0);
          setState("error");
        });
    }, ACTOR_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [text, short, context.key]);

  // Остаток выдачи — подпись ПОД списком, а не строка в нём: выбрать её
  // нельзя, и стоять среди хозяйств она не должна.
  const footer =
    hidden > 0
      ? t(m.actorMore, { count: hidden })
      : null;

  return (
    <ComboBox<ActorRow>
      label={t(m.actorLabel)}
      placeholder={t(m.actorPlaceholder)}
      inputValue={query}
      onInputChange={(next) => {
        setQuery(next);
        // Пустое поле — это снятый фильтр, а не «оставим прошлый».
        if (next.trim() === "" && value) onChange(null);
      }}
      items={hits}
      itemKey={(row) => String(row.id)}
      itemLabel={(row) => row.name ?? t(m.actorUnnamed, { id: row.id })}
      selectedKey={value}
      onSelect={(row) => {
        if (!row) {
          onChange(null);
          return;
        }
        // Имя уже стоит в поле — второй раз его запрашивать незачем.
        named.current = String(row.id);
        onChange(String(row.id));
      }}
      isLoading={!short && state === "loading"}
      emptyState={
        short
          ? null
          : state === "error"
            ? t(m.actorSearchFailed)
            : t(m.actorNothing)
      }
      footer={footer}
      explain={t(m.actorExplain)}
    />
  );
}

/* ------------------------------------------------------------------ */
/* Форма суждения                                                      */
/* ------------------------------------------------------------------ */

function VerdictForm({
  busy,
  scope,
  onSubmit,
}: {
  busy: boolean;
  scope: string;
  onSubmit: (outcome: string, reason: string) => Promise<void> | void;
}) {
  const t = useT();
  const [outcome, setOutcome] = useState<string>("confirmed");
  const [reason, setReason] = useState("");
  const ready = canSubmit(outcome, reason);
  const hint = outcomeHint(outcome);

  return (
    <div className={styles.verdict}>
      <div className={styles.verdictField}>
        <span className={styles.verdictLabel}>{t(m.verdictLabel, { scope })}</span>
        <Select
          aria-label={t(m.outcomeAria)}
          value={outcome}
          options={outcomeOptions()}
          isDisabled={busy}
          onChange={(value) => setOutcome(value)}
        />
      </div>

      <div className={`${styles.verdictField} ${styles.verdictReason}`}>
        <TextField
          label={needsReason(outcome) ? t(m.reasonRequired) : t(m.reasonOptional)}
          value={reason}
          isDisabled={busy}
          isRequired={needsReason(outcome)}
          placeholder={
            needsReason(outcome)
              ? t(m.reasonPlaceholderRequired)
              : t(m.reasonPlaceholderOptional)
          }
          onChange={setReason}
          explain={t(m.reasonExplain)}
        />
      </div>

      <Button
        variant="primary"
        disabled={busy || !ready}
        onClick={() => void onSubmit(outcome, reason)}
      >
        {t(m.save)}
      </Button>

      {hint ? <p className={styles.hint}>{hint}</p> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Раскрытие по контурам: два утверждения рядом                        */
/* ------------------------------------------------------------------ */

function ContourVerdicts({
  farmObligationId,
  canVerify,
  onChanged,
}: {
  farmObligationId: number;
  canVerify: boolean;
  onChanged: () => void;
}) {
  const t = useT();
  const context = useAnalysisContext();
  const [generation, setGeneration] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const detail = useRequest<FarmObligationDetail>(
    () => api.farmObligation(farmObligationId, context.params),
    [context.key, farmObligationId, generation],
  );

  const refresh = () => {
    setGeneration((count) => count + 1);
    onChanged();
  };

  if (detail.error && !detail.data) {
    return (
      <RequestErrorPanel
        error={detail.error}
        title={t(m.contoursFailed)}
        action={<Button onClick={detail.reload}>{t(COMMON.retry)}</Button>}
      />
    );
  }
  if (!detail.data) {
    return <DataStatePanel state="loading" title={t(m.contoursLoading)} />;
  }

  const contours = detail.data.contours;
  const shown = showAll ? contours : contours.slice(0, CONTOURS_SHOWN);

  return (
    <div className={features.stack}>
      <p className={styles.hint}>
        {t(m.contoursIntro)}
      </p>
      {shown.map((row) => (
        <ContourClaim
          key={row.obligation_id}
          row={row}
          canVerify={canVerify}
          onChanged={refresh}
        />
      ))}
      {contours.length > CONTOURS_SHOWN ? (
        <p className={styles.hint}>
          {t(m.contoursShown, { shown: shown.length, total: contours.length })}{" "}
          {showAll ? null : (
            <Button onClick={() => setShowAll(true)}>{t(m.showAll)}</Button>
          )}
        </p>
      ) : null}
    </div>
  );
}

function ContourClaim({
  row,
  canVerify,
  onChanged,
}: {
  row: ObligationContourOut;
  canVerify: boolean;
  onChanged: () => void;
}) {
  const t = useT();
  const context = useAnalysisContext();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const state = stateView(row.state);
  const pair = claimPair(row);
  const progress = progressLabel(row.progress);

  const submit = async (outcome: string, reason: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.verifyObligation(
        row.obligation_id,
        { outcome, note: reason, occurred_on: today() },
        context.params,
      );
      onChanged();
    } catch (cause) {
      setError(cause as ApiError);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title={
        <>
          {row.contour_part_id
            ? t(m.contourTitlePart, { no: row.contour_no, part: row.contour_part_id })
            : t(m.contourTitle, { no: row.contour_no })}{" "}
          <Badge tone={state.attention ? "attention" : "neutral"}>
            {state.label}
          </Badge>
          {row.awaiting_verification ? (
            <>
              {" "}
              <Badge title={t(m.awaitingBadgeTitle)}>{t(m.awaitingBadge)}</Badge>
            </>
          ) : null}
        </>
      }
    >
      <div className={styles.claims}>
        <div className={styles.claim}>
          <span className={styles.claimTitle}>{t(m.claimFarm)}</span>
          {pair.reported.progress === null ? (
            <span className={styles.claimBody}>
              {t(m.claimNoReport)}
            </span>
          ) : (
            <>
              <span className={styles.claimBody}>
                {pair.reported.on
                  ? t(m.claimProgressDate, {
                      progress: progress.label,
                      date: dateLong(pair.reported.on),
                    })
                  : progress.label}
              </span>
              <span className={styles.claimMeta}>
                {pair.reported.onBehalf
                  ? t(m.claimByBehalf, { who: pair.reported.by ?? t(m.authorUnknown) })
                  : t(m.claimBy, { who: pair.reported.by ?? t(m.authorUnknown) })}
              </span>
            </>
          )}
          <span className={styles.claimMeta}>
            {t(m.claimArea, { area: () => <Area microha={row.area_microha} /> })}
          </span>
        </div>

        <div className={styles.claim}>
          <span className={styles.claimTitle}>{t(m.claimVerifier)}</span>
          {pair.verifications.length === 0 ? (
            <span className={styles.claimBody}>
              {row.awaiting_verification ? t(m.claimWaiting) : t(m.claimNone)}
            </span>
          ) : (
            pair.verifications.map((claim) => {
              const said = outcomePast(claim.outcome);
              const who = roleView(claim.verifier_role);
              return (
                <div key={claim.verification_id}>
                  <span className={styles.claimBody}>
                    <Badge tone={said.attention ? "attention" : "neutral"}>
                      {said.label}
                    </Badge>{" "}
                    {dateLong(claim.occurred_on)}
                  </span>
                  <div className={styles.claimMeta}>
                    {claim.from_bulk_verification_id
                      ? t(m.claimVerifierBulk, {
                          who: claim.verifier ?? t(m.authorUnknown),
                          role: who.label,
                        })
                      : t(m.claimVerifierOne, {
                          who: claim.verifier ?? t(m.authorUnknown),
                          role: who.label,
                        })}
                  </div>
                  {claim.note ? (
                    <div className={styles.claimMeta}>{t(m.claimNote, { note: claim.note })}</div>
                  ) : null}
                </div>
              );
            })
          )}
        </div>
      </div>

      {pair.disputed ? (
        <InlineAlert title={t(m.disputedTitle)}>{t(m.disputedText)}</InlineAlert>
      ) : null}

      {canVerify && row.execution_id !== null ? (
        <VerdictForm busy={busy} scope={t(m.scopeContour)} onSubmit={submit} />
      ) : null}
      {canVerify && row.execution_id === null ? (
        <p className={styles.hint}>
          {t(m.nothingToCheck)}
        </p>
      ) : null}

      {error ? (
        <InlineAlert title={t(m.notAccepted)}>{error.message}</InlineAlert>
      ) : null}
    </Card>
  );
}

/** Дата суждения по умолчанию — сегодня: это утверждение автора, не догадка. */
function today(): string {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  const day = `${now.getDate()}`.padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}
